import 'regenerator-runtime/runtime';
import React, { useLayoutEffect, useState } from 'react';
import { appContext } from '../AppContext';
import { GET_USERINFO_SUCCESS } from '../actions';
import { GetUserManager, HandleAuthentication } from '../auth/AuthService';
import { User } from 'oidc-client-ts';
import Loader from '../components/loader';
import { useNavigate } from 'react-router-dom';

export type AuthContextValue = {
  authenticationError: string;
  isClientAuthenticated: boolean;
  isIndependentUser: boolean;
  /**
   * An Independent User (IU) who hasn't picked a client and only viewing
   * their IU profile.
   */
  isPartialIuAuthenticated: boolean;
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider(props: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  // represents that the user is fully authenticated with a client selected
  // TODO: come up with a better var name 🤷🏻‍♂️
  const [isClientAuthenticated, setIsClientAuthenticated] = useState(false);
  const [isPartialIuAuthenticated, setIsPartialIuAuthenticated] = useState(false);
  const [isIndependentUser, setIsIndependentUser] = useState(false);
  const [authenticationError, setAuthenticationError] = useState<string>();

  const onUserLoaded = (user: User) => {
    const hasIuid = !!user.profile.iuid;
    const hasGfClient = !!user.profile.goformz_client_id;

    // order matters here since each state change will cause a render
    setIsPartialIuAuthenticated(hasIuid && !hasGfClient);
    setIsClientAuthenticated(hasGfClient);
    setIsIndependentUser(hasIuid);
    setLoading(false);

    // todo - replace with useDispatch
    appContext.store.dispatch({
      type: GET_USERINFO_SUCCESS,
      payload: user.profile
    });
  };

  const onUserUnloaded = () => {
    setIsClientAuthenticated(false);
    setIsIndependentUser(false);
  };

  useLayoutEffect(() => {
    const userManager = GetUserManager();
    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);

    HandleAuthentication(setAuthenticationError, { navigate });

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ isClientAuthenticated, authenticationError, isIndependentUser, isPartialIuAuthenticated }} {...props}>
      {/* with the v6 router, here we are showing the loading spinner when the app is waiting to validate the token and authenticate the user */}
      {/* AuthProvider is the top level component for all authenticated child routes in our app */}
      {loading ? <Loader isLoading /> : props.children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error(`useAuth must be used within an AuthProvider`);
  }

  return context;
}
